import React, { useState } from 'react';
import _Icon from '../../../components/AppIcon';
import _Button from '../../../components/ui/Button';

const AboutSection = ({ profile = {} }) => {
  // Helper function to format experience category
  const formatExperienceCategory = () => {
    if (!profile?.experience_level) return '';
    
    const category = profile.experience_level.toLowerCase();
    switch (category) {
      case 'entry':
        return 'Entry Level';
      case 'intermediate':
        return 'Intermediate Level';
      case 'advanced':
        return 'Advanced Level';
      case 'expert':
        return 'Expert Level';
      default:
        return profile.experience_level;
    }
  };
  
  // Helper function to format availability status
  const formatAvailabilityStatus = () => {
    if (!profile?.availability_status) return 'Not specified';
    if (typeof profile.availability_status !== 'string') return 'Not specified';
    
    const status = profile.availability_status.toLowerCase();
    switch (status) {
      case 'available':
        return 'Available for work';
      case 'partially_available':
        return 'Partially available';
      case 'busy':
        return 'Limited availability';
      case 'unavailable':
        return 'Currently unavailable';
      default:
        return profile.availability_status;
    }
  };
  const [bioExpanded, setBioExpanded] = useState(false);
  
  const toggleBio = () => setBioExpanded(!bioExpanded);
  
  const _shortBio = profile?.bio ? profile.bio.substring(0, 300) : '';
  const _shouldShowReadMore = profile?.bio && profile.bio.length > 300;

  return (
    <section className="content-container py-12 flex gap-6 flex-col" id="about">
      <div className="mx-auto w-full">
      <h2 className="text-2xl font-bold mb-6">About Me</h2>
      
      {/* Bio */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <p className={`text-text-secondary leading-relaxed ${!bioExpanded ? 'line-clamp-4' : ''}`}>
          {profile?.bio || 'No bio provided.'}
          </p>
        {profile?.bio && profile.bio.length > 300 && (
          <button 
            onClick={toggleBio}
            className="text-primary-600 font-medium mt-2 flex items-center"
          >
            {bioExpanded ? 'Show less' : 'Read more'}
            <_Icon 
              name={bioExpanded ? 'ChevronUp' : 'ChevronDown'} 
              size={16} 
              className="ml-1" 
            />
          </button>
        )}
        </div>

        {/* Certifications */}
        {profile?.certifications && Array.isArray(profile.certifications) && profile.certifications.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm p-5 col-span-1">
            <div className="flex items-center mb-3">
              <div className="bg-yellow-50 p-2 rounded-full mr-3">
                <_Icon name="Award" size={20} className="text-yellow-600" />
              </div>
              <h4 className="font-semibold">Certifications</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.certifications.map((cert, index) => {
                // Safely extract certification name and URL
                const certName = typeof cert === 'object' && cert !== null ? 
                  (cert.name || cert.title || JSON.stringify(cert)) : cert;
                const certUrl = typeof cert === 'object' && cert !== null ? cert.url : null;
                
                if (certUrl) {
                  return (
                    <a
                      key={index}
                      href={certUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-secondary-50 text-secondary-700 px-3 py-1 rounded-full text-sm hover:bg-secondary-100 hover:text-secondary-800 transition-colors inline-flex items-center gap-1"
                    >
                      {certName}
                      <_Icon name="ExternalLink" size={12} />
                    </a>
                  );
                } else {
                  return (
                    <span 
                      key={index} 
                      className="bg-secondary-50 text-secondary-700 px-3 py-1 rounded-full text-sm"
                    >
                      {certName}
                    </span>
                  );
                }
              })}
            </div>
          </div>
          
        )}
      </div>
    <div className="mx-auto">
      {/* Quick Facts */}
      <h2 className="text-2xl font-bold mb-4">Quick Facts</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Experience */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center mb-3">
            <div className="bg-blue-50 p-2 rounded-full mr-3">
              <_Icon name="Briefcase" size={20} className="text-blue-600" />
            </div>
            <h4 className="font-semibold">Experience</h4>
          </div>
          <p className="text-text-secondary">
            {profile?.experience_years ? `${profile.experience_years} year${profile.experience_years !== 1 ? 's' : ''} of experience` : 'Experience not specified'} as a {profile?.title}
            {profile?.experience_level && (
              <span className="block mt-1 text-sm text-text-muted">
                Position: {formatExperienceCategory()}
              </span>
            )}
          </p>
        </div>
        
        {/* Languages */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center mb-3">
            <div className="bg-green-50 p-2 rounded-full mr-3">
              <_Icon name="Globe" size={20} className="text-green-600" />
            </div>
            <h4 className="font-semibold">Languages</h4>
          </div>
          {profile?.languages && Array.isArray(profile.languages) && profile.languages.length > 0 ? (
            <ul className="space-y-2">
              {profile.languages.map((language, index) => (
                <li key={index} className="flex justify-between">
                  <span className="text-text-secondary">
                    {typeof language === 'object' ? language.name : language}
                  </span>
                  <span className="text-text-muted">
                    {typeof language === 'object' ? language.level : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-text-secondary">No languages specified</p>
          )}
        </div>
        
        {/* Availability */}
        <div className="bg-white rounded-lg shadow-sm p-5">
          <div className="flex items-center mb-3">
            <div className="bg-purple-50 p-2 rounded-full mr-3">
              <_Icon name="Calendar" size={20} className="text-purple-600" />
            </div>
            <h4 className="font-semibold">Availability</h4>
          </div>
          <p className="text-text-secondary mb-2">
            Status: <span className="font-medium">{formatAvailabilityStatus()}</span>
          </p>
          <p className="text-text-secondary mb-2">
            Hours: <span className="font-medium">{profile?.availability_hours || 'Not specified'}</span>
          </p>
          <p className="text-text-secondary">
            Working hours: <span className="font-medium">{profile?.workingHours || 'Flexible'}</span>
          </p>
        </div>
        
        {/* Industry Specialization */}
        {profile?.industry_specialization && (
          Array.isArray(profile.industry_specialization) ? 
          profile.industry_specialization.length > 0 : true) && (
          <div className="bg-white rounded-lg shadow-sm p-5">
            <div className="flex items-center mb-3">
              <div className="bg-indigo-50 p-2 rounded-full mr-3">
                <_Icon name="Layers" size={20} className="text-indigo-600" />
              </div>
              <h4 className="font-semibold">Industry Specialization</h4>
            </div>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(profile.industry_specialization) ? 
                profile.industry_specialization.map((industry, index) => (
                  <span key={index} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm">
                    {industry}
                  </span>
                )) : 
                <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm">
                  {profile.industry_specialization}
                </span>
              }
            </div>
          </div>
        )}
        
        
      </div>
      </div>
    </section>
  );
};

export default AboutSection;
